import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { RequireRole } from '@/features/auth/RequireRole';
import { rolesForPath } from '@/config/navigation';

import LoginPage from '@/features/auth/LoginPage';
import ForgotPasswordPage from '@/features/auth/ForgotPasswordPage';
import UpdatePasswordPage from '@/features/auth/UpdatePasswordPage';
import SettingsPage from '@/features/admin/SettingsPage';
import LeadsPage from '@/features/leads/LeadsPage';
import MyTasksPage from '@/features/tasks/MyTasksPage';
import AlertsPage from '@/features/tasks/AlertsPage';
import LeadDetailPage from '@/features/leads/LeadDetailPage';
import InventoryPage from '@/features/inventory/InventoryPage';
import CommissionsPage from '@/features/deals/CommissionsPage';
import CampaignsPage from '@/features/campaigns/CampaignsPage';
import ReportsPage from '@/features/reports/ReportsPage';
import UsersPage from '@/features/admin/UsersPage';
import TeamsPage from '@/features/admin/TeamsPage';
import DashboardPage from '@/pages/DashboardPage';
import PlaceholderPage from '@/pages/PlaceholderPage';
import ForbiddenPage from '@/pages/ForbiddenPage';
import NotFoundPage from '@/pages/NotFoundPage';

/**
 * Route table.
 *
 * Guard roles come from the same navigation config the sidebar reads, so a menu item
 * and its route can never disagree about who is allowed in. Both are presentation
 * only — RLS is what actually protects the rows.
 */
export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/update-password" element={<UpdatePasswordPage />} />

      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route index element={<DashboardPage />} />
          <Route path="notifications" element={<PlaceholderPage titleKey="nav.notifications" />} />
          <Route path="forbidden" element={<ForbiddenPage />} />

          <Route element={<RequireRole allow={rolesForPath('/leads')} />}>
            <Route path="leads" element={<LeadsPage />} />
            <Route path="leads/:id" element={<LeadDetailPage />} />
          </Route>

          <Route element={<RequireRole allow={rolesForPath('/inventory')} />}>
            <Route path="inventory" element={<InventoryPage />} />
          </Route>

          <Route element={<RequireRole allow={rolesForPath('/reports')} />}>
            <Route path="reports" element={<ReportsPage />} />
          </Route>

          <Route element={<RequireRole allow={rolesForPath('/campaigns')} />}>
            <Route path="campaigns" element={<CampaignsPage />} />
          </Route>

          <Route element={<RequireRole allow={rolesForPath('/commissions')} />}>
            <Route path="commissions" element={<CommissionsPage />} />
          </Route>

          <Route element={<RequireRole allow={rolesForPath('/tasks')} />}>
            <Route path="tasks" element={<MyTasksPage />} />
          </Route>

          <Route element={<RequireRole allow={rolesForPath('/alerts')} />}>
            <Route path="alerts" element={<AlertsPage />} />
          </Route>

          <Route element={<RequireRole allow={rolesForPath('/admin/users')} />}>
            <Route path="admin/users" element={<UsersPage />} />
          </Route>

          <Route element={<RequireRole allow={rolesForPath('/admin/teams')} />}>
            <Route path="admin/teams" element={<TeamsPage />} />
          </Route>

          <Route element={<RequireRole allow={rolesForPath('/admin/audit')} />}>
            <Route path="admin/audit" element={<PlaceholderPage titleKey="nav.audit" />} />
          </Route>

          <Route element={<RequireRole allow={rolesForPath('/admin/settings')} />}>
            <Route path="admin/settings" element={<SettingsPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
